import {
  getRedeemableRule,
  normalizeRedeemableCode,
} from './redeemables.js'

const SHOPIFY_API_VERSION = '2025-10'
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = String(
  import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN || '',
).trim()

function normalizeShopifyStoreDomain(value) {
  const trimmedValue = String(value || '').trim()

  if (!trimmedValue) {
    return ''
  }

  const valueWithProtocol = /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`

  try {
    return new URL(valueWithProtocol).hostname.trim()
  } catch {
    return trimmedValue
      .replace(/^https?:\/\//i, '')
      .replace(/\/.*$/, '')
      .trim()
  }
}

const SHOPIFY_STORE_DOMAIN = normalizeShopifyStoreDomain(
  import.meta.env.VITE_SHOPIFY_STORE_DOMAIN || '',
)

const VIBE_SELECTION_ALIASES = {
  her: ['her'],
  hervibe: ['her'],
  his: ['his'],
  hisvibe: ['his'],
  boysvibe: ['his'],
  mini: ['mini'],
  minivibe: ['mini'],
  hype: ['hype'],
  hypevibe: ['hype'],
  uni: ['uni'],
  univibe: ['uni'],
  premiumvibe: ['premiumvibe'],
}

function normalizeText(value) {
  return String(value || '').trim()
}

function extractMoneyAmount(value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (typeof value === 'object') {
    return normalizeText(value?.amount ?? value?.value ?? value?.price ?? '')
  }

  return normalizeText(value)
}

function parseMoneyValue(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number(String(value).replace(/[^0-9.-]/g, ''))

  return Number.isFinite(numericValue) ? numericValue : null
}

export function resolveVariantPricing(product, variant = null) {
  const variants = Array.isArray(product?.variants) ? product.variants : []
  const fallbackVariant =
    variant ??
    product?.variant ??
    variants[0] ??
    product?.variants?.edges?.[0]?.node ??
    null

  const priceAmount = extractMoneyAmount(
    fallbackVariant?.price?.amount ??
      fallbackVariant?.price ??
      product?.price?.amount ??
      product?.price ??
      product?.base_price ??
      product?.price_inr ??
      product?.priceRange?.minVariantPrice?.amount ??
      product?.priceRange?.maxVariantPrice?.amount ??
      '',
  )
  const compareAtAmount = extractMoneyAmount(
    fallbackVariant?.compareAtPrice?.amount ??
      fallbackVariant?.compareAtPrice ??
      fallbackVariant?.compare_at_price?.amount ??
      fallbackVariant?.compare_at_price ??
      product?.compareAtPrice?.amount ??
      product?.compareAtPrice ??
      product?.compare_at_price?.amount ??
      product?.compare_at_price ??
      '',
  )
  const priceValue = parseMoneyValue(priceAmount)
  const compareAtValue = parseMoneyValue(compareAtAmount)
  const hasCompareAt =
    Number.isFinite(priceValue) &&
    Number.isFinite(compareAtValue) &&
    compareAtValue > priceValue
  const currencyCode = normalizeText(
    fallbackVariant?.price?.currencyCode ??
      fallbackVariant?.compareAtPrice?.currencyCode ??
      fallbackVariant?.compare_at_price?.currencyCode ??
      product?.price?.currencyCode ??
      product?.compareAtPrice?.currencyCode ??
      product?.compare_at_price?.currencyCode ??
      product?.priceRange?.minVariantPrice?.currencyCode ??
      product?.priceRange?.maxVariantPrice?.currencyCode ??
      '',
  )

  return {
    priceAmount,
    compareAtAmount: hasCompareAt ? compareAtAmount : '',
    currencyCode,
    hasCompareAt,
  }
}

export function normalizeShopifyProductId(value) {
  const trimmedValue = normalizeText(value)

  if (!trimmedValue) {
    return ''
  }

  if (trimmedValue.startsWith('gid://shopify/')) {
    const lastSegment = trimmedValue.split('/').pop()?.trim() ?? ''

    if (lastSegment) {
      return lastSegment
    }
  }

  return trimmedValue
}

function normalizeVibeToken(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((tag) => normalizeText(tag)).filter(Boolean)
  }

  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => normalizeText(tag))
      .filter(Boolean)
  }

  return []
}

function collectVibeValues(source) {
  if (Array.isArray(source)) {
    return source.flatMap((value) => collectVibeValues(value))
  }

  if (source && typeof source === 'object') {
    return Object.values(source).flatMap((value) => collectVibeValues(value))
  }

  const normalizedSource = normalizeText(source)

  if (!normalizedSource) {
    return []
  }

  if (/[\n,•|;]/.test(normalizedSource)) {
    return normalizedSource
      .split(/[\n,•|;]+/)
      .map((value) => normalizeText(value))
      .filter(Boolean)
  }

  return [normalizedSource]
}

function collectProductVibeTokens(product) {
  return Array.from(
    new Set(
      collectVibeValues([
        product?.tags,
        product?.product_tags,
        product?.node?.tags,
        product?.node?.product_tags,
      ])
        .map((value) => normalizeVibeToken(value))
        .filter(Boolean),
    ),
  )
}

function getSelectionTokens(activeVibe) {
  const normalizedSelection = normalizeVibeToken(activeVibe)

  if (
    !normalizedSelection ||
    normalizedSelection === 'all' ||
    normalizedSelection === 'allvibes'
  ) {
    return []
  }

  return VIBE_SELECTION_ALIASES[normalizedSelection] ?? [normalizedSelection]
}

export function matchesVibeSelection(product, activeVibe = 'all') {
  const selectionTokens = getSelectionTokens(activeVibe)

  if (!selectionTokens.length) {
    return true
  }

  const productTokens = collectProductVibeTokens(product)

  if (!productTokens.length) {
    return false
  }

  if (selectionTokens.includes('premiumvibe')) {
    return true
  }

  const hasExactMatch = selectionTokens.some((selectionToken) =>
    productTokens.some((candidateToken) => candidateToken === selectionToken),
  )

  return hasExactMatch
}

function getShopifyEndpoint() {
  if (!SHOPIFY_STORE_DOMAIN) {
    throw new Error('VITE_SHOPIFY_STORE_DOMAIN is missing.')
  }

  if (!SHOPIFY_STOREFRONT_ACCESS_TOKEN) {
    throw new Error('VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN is missing.')
  }

  return `https://${SHOPIFY_STORE_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`
}

async function shopifyGraphQL(query, variables = {}) {
  const response = await fetch(getShopifyEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  })

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const message =
      payload?.errors?.[0]?.message ||
      `Shopify request failed with status ${response.status}.`

    throw new Error(message)
  }

  if (payload?.errors?.length) {
    throw new Error(payload.errors[0]?.message || 'Shopify request failed.')
  }

  return payload
}

function getImageNodes(product) {
  const imageEdges = Array.isArray(product?.images?.edges)
    ? product.images.edges
    : []

  return imageEdges.map((edge) => edge?.node ?? null).filter(Boolean)
}

function getVariantNodes(product) {
  return Array.isArray(product?.variants?.edges)
    ? product.variants.edges
        .map((edge) => edge?.node ?? null)
        .filter(Boolean)
    : []
}

function getOptionValue(selectedOptions, matcher) {
  return (
    selectedOptions.find((option) =>
      matcher.test(normalizeText(option?.name)),
    ) ?? null
  )
}

function normalizeVariant(variant, index = 0) {
  const selectedOptions = Array.isArray(variant?.selectedOptions)
    ? variant.selectedOptions
    : []

  const colorOption = getOptionValue(
    selectedOptions,
    /color|colour|shade|tone/i,
  )
  const sizeOption = getOptionValue(selectedOptions, /size|fit|length/i)
  const color = normalizeText(colorOption?.value)
  const size = normalizeText(sizeOption?.value)
  const imageUrl = normalizeText(variant?.image?.url)
  const priceAmount =
    normalizeText(variant?.price?.amount) || normalizeText(variant?.price)
  const compareAtPriceAmount =
    normalizeText(variant?.compareAtPrice?.amount) ||
    normalizeText(variant?.compareAtPrice)

  return {
    id: normalizeText(variant?.id),
    shopifyVariantId: normalizeText(variant?.id),
    color,
    size,
    label: normalizeText(variant?.title) || [color, size].filter(Boolean).join(' / '),
    value: normalizeText(variant?.title) || [color, size].filter(Boolean).join(' / '),
    image_url: imageUrl,
    availableForSale: Boolean(variant?.availableForSale),
    price: priceAmount,
    compareAtPrice: compareAtPriceAmount,
    compareAtPriceCurrencyCode: normalizeText(variant?.compareAtPrice?.currencyCode),
    currencyCode: normalizeText(variant?.price?.currencyCode),
    sku: normalizeText(variant?.sku),
    normalized_color: normalizeText(color).toLowerCase(),
    normalized_size: normalizeText(size).toLowerCase(),
    _variant_index: index,
    selectedOptions,
  }
}

function buildOptionList(values = []) {
  const uniqueOptions = []
  const seenLabels = new Set()

  values.forEach((value, index) => {
    const label = normalizeText(value)

    if (!label) {
      return
    }

    const normalizedLabel = label.toLowerCase()

    if (seenLabels.has(normalizedLabel)) {
      return
    }

    seenLabels.add(normalizedLabel)
    uniqueOptions.push({
      label,
      value: label,
      hex: undefined,
      index,
    })
  })

  return uniqueOptions
}

function resolveVibeCategoryFromTags(tags) {
  const normalizedTags = normalizeTags(tags).map((tag) => normalizeVibeToken(tag))

  if (
    normalizedTags.some(
      (tag) => tag === 'hervibe' || tag === 'heranime' || tag.includes('heranime'),
    )
  ) {
    return 'Her Vibe'
  }

  if (
    normalizedTags.some(
      (tag) => tag === 'hisvibe' || tag === 'hisanime' || tag.includes('hisanime'),
    )
  ) {
    return 'His Vibe'
  }

  if (normalizedTags.some((tag) => tag === 'minivibe')) {
    return 'Mini Vibe'
  }

  if (normalizedTags.some((tag) => tag === 'hypevibe')) {
    return 'Hype Vibe'
  }

  if (normalizedTags.some((tag) => tag === 'premiumvibe')) {
    return 'Premium Vibe'
  }

  if (
    normalizedTags.some(
      (tag) => tag === 'univibe' || tag === 'unisex' || tag.includes('uni'),
    )
  ) {
    return 'Uni Vibe'
  }

  return ''
}

function normalizeShopifyProduct(node) {
  const imageNodes = getImageNodes(node)
  const variants = getVariantNodes(node).map((variant, index) =>
    normalizeVariant(variant, index),
  )
  const collectionNodes = Array.isArray(node?.collections?.edges)
    ? node.collections.edges
        .map((edge) => edge?.node ?? null)
        .filter(Boolean)
    : []
  const vendor = normalizeText(node?.vendor)
  const resolvedVendor =
    vendor && vendor.toLowerCase() !== 'my store' ? vendor : 'Aikostore'

  const colors = buildOptionList(
    variants.map((variant) => variant.color).filter(Boolean),
  )
  const sizes = buildOptionList(
    variants.map((variant) => variant.size).filter(Boolean),
  )

  const firstAvailableVariant =
    variants.find((variant) => variant.availableForSale) ?? variants[0] ?? null

  const firstImage = normalizeText(node?.featuredImage?.url) || imageNodes[0]?.url || ''
  const priceAmount =
    normalizeText(node?.priceRange?.minVariantPrice?.amount) ||
    normalizeText(node?.priceRange?.maxVariantPrice?.amount) ||
    normalizeText(firstAvailableVariant?.price) ||
    ''
  const compareAtAmount = normalizeText(firstAvailableVariant?.compareAtPrice)
  const compareAtCurrencyCode = normalizeText(
    firstAvailableVariant?.compareAtPriceCurrencyCode ||
      firstAvailableVariant?.compareAtPrice?.currencyCode ||
      '',
  )
  const tags = normalizeTags(node?.tags)
  const vibeCategory = resolveVibeCategoryFromTags(tags)
  const collections = Array.from(
    new Set(
      collectionNodes.flatMap((collectionNode) =>
        [
          normalizeText(collectionNode?.title),
          normalizeText(collectionNode?.handle),
        ].filter(Boolean),
      ),
    ),
  )
  const resolvedShopifyVariantId =
    normalizeText(firstAvailableVariant?.shopifyVariantId) ||
    normalizeText(firstAvailableVariant?.id)

  return {
    id: normalizeText(node?.id),
    uuid: normalizeText(node?.id),
    handle: normalizeText(node?.handle),
    title: normalizeText(node?.title),
    name: normalizeText(node?.title),
    description: normalizeText(node?.description),
    descriptionHtml: node?.descriptionHtml ?? '',
    details: normalizeText(node?.description),
    summary: normalizeText(node?.description),
    tags,
    productType: normalizeText(node?.productType),
    product_type: normalizeText(node?.productType),
    vendor: resolvedVendor,
    collections,
    collectionTitles: collectionNodes
      .map((collectionNode) => normalizeText(collectionNode?.title))
      .filter(Boolean),
    collectionHandles: collectionNodes
      .map((collectionNode) => normalizeText(collectionNode?.handle))
      .filter(Boolean),
    priceRange: node?.priceRange ?? null,
    price: priceAmount,
    base_price: priceAmount,
    price_inr: priceAmount,
    compareAtPrice: compareAtAmount,
    compareAtPriceCurrencyCode: compareAtCurrencyCode,
    shopifyVariantId: resolvedShopifyVariantId,
    shopify_variant_id: resolvedShopifyVariantId,
    selected_variant_id: resolvedShopifyVariantId,
    image_url: firstImage,
    featuredImage: node?.featuredImage ?? null,
    images: {
      edges: imageNodes.map((imageNode) => ({
        node: imageNode,
      })),
      nodes: imageNodes,
    },
    variants,
    colors,
    sizes,
    vibe_category: vibeCategory,
    category: vibeCategory,
    product_category: vibeCategory,
    created_at: normalizeText(node?.createdAt),
    createdAt: normalizeText(node?.createdAt),
    updated_at: normalizeText(node?.updatedAt),
    updatedAt: normalizeText(node?.updatedAt),
  }
}

function matchesActiveVibe(product, activeVibe) {
  return matchesVibeSelection(product, activeVibe)
}

export function resolveCheckoutVariantId(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : []
  const selectedColor = normalizeText(
    product?.selected_color ?? product?.variant_color ?? '',
  )
  const selectedSize = normalizeText(
    product?.selected_size ?? product?.variant_size ?? '',
  )

  const matchingVariant =
    variants.find((variant) => {
      const variantColor = normalizeText(variant?.color)
      const variantSize = normalizeText(variant?.size)

      return (
        (!selectedColor || variantColor === selectedColor) &&
        (!selectedSize || variantSize === selectedSize)
      )
    }) ?? null

  const fallbackVariant =
    variants.find((variant) => variant?.availableForSale) ?? variants[0] ?? null

  return (
    normalizeText(product?.variantId) ||
    normalizeText(product?.merchandiseId) ||
    normalizeText(product?.shopifyVariantId) ||
    normalizeText(product?.shopify_variant_id) ||
    normalizeText(product?.selected_variant_id) ||
    normalizeText(product?.variant?.variantId) ||
    normalizeText(product?.variant?.shopifyVariantId) ||
    normalizeText(product?.variant?.id) ||
    matchingVariant?.shopifyVariantId ||
    matchingVariant?.id ||
    fallbackVariant?.shopifyVariantId ||
    fallbackVariant?.id ||
    normalizeText(product?.variants?.edges?.[0]?.node?.id) ||
    normalizeText(product?.variants?.edges?.[0]?.node?.shopifyVariantId) ||
    normalizeText(variants?.[0]?.variantId) ||
    normalizeText(variants?.[0]?.shopifyVariantId) ||
    normalizeText(variants?.[0]?.id) ||
    ''
  )
}

function normalizeCheckoutLineItems(items) {
  const cartItems = Array.isArray(items) ? items : items ? [items] : []
  const lineItems = []

  cartItems.forEach((item) => {
    const variantId = resolveCheckoutVariantId(item)

    if (!variantId) {
      return
    }

    const quantity = Math.max(1, Number(item?.quantity ?? 1) || 1)
    lineItems.push({
      variantId,
      quantity,
    })
  })

  return lineItems
}

function normalizeCheckoutBuyerIdentity(details = {}) {
  const buyerIdentity = {}
  const email = normalizeText(details?.email)
  const phone = normalizeText(details?.phone)
  const countryCode = normalizeText(details?.countryCode)

  if (email) {
    buyerIdentity.email = email
  }

  if (phone) {
    buyerIdentity.phone = phone
  }

  if (countryCode) {
    buyerIdentity.countryCode = countryCode
  }

  return buyerIdentity
}

const PRODUCT_LIST_QUERY = `
  query FetchProducts($first: Int!, $after: String) {
    products(first: $first, after: $after, sortKey: BEST_SELLING, reverse: true) {
      edges {
        node {
          id
          handle
          title
          tags
          description
          productType
          vendor
          createdAt
          updatedAt
          collections(first: 10) {
            edges {
              node {
                id
                title
                handle
              }
            }
          }
          featuredImage {
            url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
            altText
          }
          images(first: 10) {
            edges {
              node {
                url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
                altText
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                sku
                availableForSale
                selectedOptions {
                  name
                  value
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                image {
                  url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
                  altText
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

const COLLECTION_PRODUCT_LIST_QUERY = `
  query FetchCollectionProducts($handle: String!, $first: Int!, $after: String) {
    collection(handle: $handle) {
      id
      handle
      title
      products(first: $first, after: $after, sortKey: CREATED, reverse: true) {
        edges {
          node {
            id
            handle
            title
            tags
            description
            productType
            vendor
            createdAt
            updatedAt
            collections(first: 10) {
              edges {
                node {
                  id
                  title
                  handle
                }
              }
            }
            featuredImage {
              url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
              altText
            }
            images(first: 10) {
              edges {
                node {
                  url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
                  altText
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 50) {
              edges {
                node {
                  id
                  title
                  sku
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  image {
                    url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
                    altText
                  }
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

const PRODUCT_BY_HANDLE_QUERY = `
  query FetchProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      handle
      title
      tags
      description
      descriptionHtml
      productType
      vendor
      createdAt
      updatedAt
      collections(first: 10) {
        edges {
          node {
            id
            title
            handle
          }
        }
      }
      featuredImage {
        url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
        altText
      }
      images(first: 10) {
        edges {
          node {
            url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
            altText
          }
        }
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            sku
            availableForSale
            selectedOptions {
              name
              value
            }
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
              altText
            }
          }
        }
      }
    }
  }
`

const PRODUCT_NODES_BY_IDS_QUERY = `
  query FetchProductsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        tags
        description
        productType
        vendor
        createdAt
        updatedAt
        collections(first: 10) {
          edges {
            node {
              id
              title
              handle
            }
          }
        }
        featuredImage {
          url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
          altText
        }
        images(first: 10) {
          edges {
            node {
              url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
              altText
            }
          }
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 50) {
          edges {
            node {
              id
              title
              sku
              availableForSale
              selectedOptions {
                name
                value
              }
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              image {
                url(transform: { maxWidth: 600, maxHeight: 800, crop: CENTER })
                altText
              }
            }
          }
        }
      }
    }
  }
`

const CART_CREATE_MUTATION = `
  mutation CartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`

async function fetchShopifyProductPage(first, after = null) {
  const payload = await shopifyGraphQL(PRODUCT_LIST_QUERY, { first, after })
  const edges = Array.isArray(payload?.data?.products?.edges)
    ? payload.data.products.edges
    : []
  const pageInfo = payload?.data?.products?.pageInfo ?? {}

  return {
    edges,
    pageInfo,
  }
}

async function fetchShopifyCollectionProductPage(
  collectionHandle,
  first,
  after = null,
) {
  const payload = await shopifyGraphQL(COLLECTION_PRODUCT_LIST_QUERY, {
    handle: collectionHandle,
    first,
    after,
  })
  const collection = payload?.data?.collection ?? null
  const edges = Array.isArray(collection?.products?.edges)
    ? collection.products.edges
    : []
  const pageInfo = collection?.products?.pageInfo ?? {}

  return {
    collection,
    edges,
    pageInfo,
  }
}

export async function fetchShopifyProducts(activeVibe = 'all', first = 100) {
  const { edges } = await fetchShopifyProductPage(first)

  return edges
    .map((edge) => normalizeShopifyProduct(edge?.node))
    .filter(Boolean)
    .filter((product) => matchesActiveVibe(product, activeVibe))
}

export async function fetchAllShopifyProducts(first = 100) {
  const pageSize = Math.max(1, Math.min(Number(first) || 100, 250))
  const collectedProducts = []
  const seenProductIds = new Set()
  let after = null

  while (true) {
    const { edges, pageInfo } = await fetchShopifyProductPage(pageSize, after)

    edges
      .map((edge) => normalizeShopifyProduct(edge?.node))
      .filter(Boolean)
      .forEach((product) => {
        const productId = normalizeShopifyProductId(product?.id || product?.uuid)

        if (productId && seenProductIds.has(productId)) {
          return
        }

        if (productId) {
          seenProductIds.add(productId)
        }

        collectedProducts.push(product)
      })

    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) {
      break
    }

    after = pageInfo.endCursor
  }

  return collectedProducts
}

export async function fetchAllShopifyCollectionProductsByHandle(
  collectionHandle,
  first = 100,
) {
  const normalizedCollectionHandle = normalizeText(collectionHandle).toLowerCase()

  if (!normalizedCollectionHandle) {
    return []
  }

  const pageSize = Math.max(1, Math.min(Number(first) || 100, 250))
  const collectedProducts = []
  const seenProductIds = new Set()
  let after = null

  while (true) {
    const { collection, edges, pageInfo } =
      await fetchShopifyCollectionProductPage(
        normalizedCollectionHandle,
        pageSize,
        after,
      )

    if (!collection) {
      break
    }

    edges
      .map((edge) => normalizeShopifyProduct(edge?.node))
      .filter(Boolean)
      .forEach((product) => {
        const productId = normalizeShopifyProductId(product?.id || product?.uuid)

        if (productId && seenProductIds.has(productId)) {
          return
        }

        if (productId) {
          seenProductIds.add(productId)
        }

        collectedProducts.push({
          ...product,
          collectionHandle: normalizedCollectionHandle,
          collection_handle: normalizedCollectionHandle,
          collections: Array.from(
            new Set([...(product?.collections ?? []), normalizedCollectionHandle].filter(Boolean)),
          ),
        })
      })

    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) {
      break
    }

    after = pageInfo.endCursor
  }

  return collectedProducts
}

export async function fetchShopifyProductsByIds(productIds = []) {
  const normalizedIds = Array.isArray(productIds)
    ? productIds
        .map((value) => normalizeShopifyProductId(value))
        .filter(Boolean)
    : []

  if (!normalizedIds.length) {
    return []
  }

  const formattedIds = normalizedIds.map(
    (id) => `gid://shopify/Product/${String(id).trim()}`,
  )

  const payload = await shopifyGraphQL(PRODUCT_NODES_BY_IDS_QUERY, {
    ids: formattedIds,
  })

  const nodes = Array.isArray(payload?.data?.nodes)
    ? payload.data.nodes.filter(Boolean)
    : []

  return nodes.map((node) => normalizeShopifyProduct(node)).filter(Boolean)
}

export async function fetchShopifyProductByHandle(handle) {
  const trimmedHandle = normalizeText(handle)

  if (!trimmedHandle) {
    throw new Error('A product handle is required.')
  }

  const payload = await shopifyGraphQL(PRODUCT_BY_HANDLE_QUERY, {
    handle: trimmedHandle,
  })

  const product = payload?.data?.productByHandle ?? null

  return product ? normalizeShopifyProduct(product) : null
}

export async function createShopifyCheckoutSession(items, buyerDetails = {}) {
  const lineItems = normalizeCheckoutLineItems(items)

  if (!lineItems.length) {
    throw new Error('No Shopify variant available for checkout.')
  }

  const buyerIdentity = normalizeCheckoutBuyerIdentity(buyerDetails)
  const redeemableCode = normalizeRedeemableCode(
    buyerDetails?.discountCode || buyerDetails?.giftCardCode || '',
  )
  const redeemableRule = getRedeemableRule(redeemableCode)
  const discountCodes =
    redeemableCode && redeemableRule?.kind !== 'gift_card'
      ? [redeemableRule?.code || redeemableCode]
      : []
  const giftCardCodes =
    redeemableCode && redeemableRule?.kind === 'gift_card'
      ? [redeemableRule?.code || redeemableCode]
      : []
  const attributes = redeemableCode
    ? [
        {
          key: 'aikovibe_redeemable_code',
          value: redeemableRule?.code || redeemableCode,
        },
      ]
    : []
  const input = {
    lines: lineItems.map((item) => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
    })),
    ...(Object.keys(buyerIdentity).length ? { buyerIdentity } : {}),
    ...(discountCodes.length ? { discountCodes } : {}),
    ...(giftCardCodes.length ? { giftCardCodes } : {}),
    ...(attributes.length ? { attributes } : {}),
  }

  const payload = await shopifyGraphQL(CART_CREATE_MUTATION, {
    input,
  })

  const userErrors = payload?.data?.cartCreate?.userErrors ?? []

  if (userErrors.length) {
    const messages = userErrors
      .map((error) => normalizeText(error?.message))
      .filter(Boolean)

    throw new Error(messages[0] || 'Unable to create Shopify checkout.')
  }

  const checkoutUrl = normalizeText(
    payload?.data?.cartCreate?.cart?.checkoutUrl,
  )

  if (!checkoutUrl) {
    throw new Error('Shopify did not return a checkout URL.')
  }

  return {
    checkoutUrl,
    id: normalizeText(payload?.data?.cartCreate?.cart?.id),
    webUrl: checkoutUrl,
  }
}
